import React from "react";
import {
  Alert,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  getExperimentSeverityWarnings,
  getMaxDeclaredSeverity,
} from "../utils/severityValidation";
import { getStrainColumns, getGroupCount } from "./Section29Form";

const SEVERITY_LEVELS = [
  { key: "sd0", label: "SD0" },
  { key: "sd1", label: "SD1" },
  { key: "sd2", label: "SD2" },
  { key: "sd3", label: "SD3" },
];

const SEVERITY_GUIDANCE = [
  {
    key: "sd0",
    title: "Severity degree 0: No constraint",
    description: "Use this when animals are not expected to experience stress, pain, or burden from the experiment.",
  },
  {
    key: "sd1",
    title: "Severity degree 1: Light stress",
    description: "Use this for mild or short-lasting stress with limited impact on the animal.",
  },
  {
    key: "sd2",
    title: "Severity degree 2: Medium stress",
    description: "Use this for moderate stress, repeated burden, or procedures with a noticeable impact on well-being.",
  },
  {
    key: "sd3",
    title: "Severity degree 3: Severe stress",
    description: "Use this for severe stress or procedures expected to cause major burden or suffering.",
  },
];

function Section36Form({ index, data, setData }) {
  const exp = data[index];
  const strainColumns = getStrainColumns(data);
  const severityDistribution = exp.severityDistribution || {};
  const groups = exp.groups || [];

  // Section 29 totals per column
  const section29Totals = strainColumns.map(({ id }) =>
    groups.reduce((sum, g) => sum + (parseInt(getGroupCount(g, id), 10) || 0), 0)
  );
  const section29Grand = section29Totals.reduce((a, b) => a + b, 0);

  // Section 36 totals per column
  const section36Totals = strainColumns.map(({ id }) =>
    SEVERITY_LEVELS.reduce(
      (sum, { key }) => sum + (parseInt((severityDistribution[key] || {})[id], 10) || 0),
      0
    )
  );
  const section36Grand = section36Totals.reduce((a, b) => a + b, 0);

  const hasMismatch = strainColumns.some(({ id }, ci) => section29Totals[ci] !== section36Totals[ci]);

  const getRowTotal = (severityKey) => {
    const row = severityDistribution[severityKey] || {};
    return strainColumns.reduce((sum, { id }) => sum + (parseInt(row[id], 10) || 0), 0);
  };

  const updateSeverityValue = (severityKey, colId, value) => {
    const newData = [...data];
    const currentDistribution = newData[index].severityDistribution || {};
    newData[index] = {
      ...newData[index],
      severityDistribution: {
        ...currentDistribution,
        [severityKey]: {
          ...(currentDistribution[severityKey] || {}),
          [colId]: value,
        },
      },
    };
    setData(newData);
  };

  const severityWarnings = getExperimentSeverityWarnings(exp);
  const maxSeverity = getMaxDeclaredSeverity(exp);
  const recommendedJustification = severityWarnings.some((item) => item.code === "missing-justification");

  return (
    <Box sx={{ mt: 4, p: 3, border: "1px solid #ccc", borderRadius: 2, backgroundColor: "#fafafa" }}>
      <Box sx={{ mb: 2, px: 2, py: 1, borderLeft: "4px solid", borderColor: "primary.main", backgroundColor: "primary.50" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
          Section 36 — Distribution by Degree of Severity
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the expected number of animals per severity degree for this experiment. The totals should match the
        animals declared in Section 29.
      </Typography>

      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#fff7fb",
          border: "1px solid #f4c6da",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          How to choose the severity degree
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          For each experiment, enter how many animals are expected to fall into each severity degree. Use the
          expected maximum severity for the animals in that experiment.
        </Typography>
        {SEVERITY_GUIDANCE.map((item, idx) => (
          <Box key={item.key} sx={{ mb: idx === SEVERITY_GUIDANCE.length - 1 ? 0 : 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
        What to enter in the table
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fill in the number of animals for each severity row, split by {strainColumns.map((c) => c.label).join(", ")}.
        The total of all severity rows should equal the total number of animals declared for this experiment in Section 29.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Severity</TableCell>
              {strainColumns.map((col) => (
                <TableCell key={col.id} align="center" sx={{ fontWeight: "bold" }}>{col.label}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SEVERITY_LEVELS.map(({ key, label }) => {
              const row = severityDistribution[key] || {};
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: "medium" }}>{label}</TableCell>
                  {strainColumns.map(({ id }) => (
                    <TableCell key={id} align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={row[id] || ""}
                        onChange={(e) => updateSeverityValue(key, id, e.target.value)}
                        variant="standard"
                        inputProps={{ min: 0, style: { textAlign: "center", width: "60px" } }}
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {getRowTotal(key)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: "#fce4ec" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Totals</TableCell>
              {section36Totals.map((total, ci) => (
                <TableCell key={strainColumns[ci].id} align="center" sx={{ fontWeight: "bold" }}>{total}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>{section36Grand}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Section 29 total for this experiment: <strong>{section29Grand}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {strainColumns.map((col, ci) => `${col.label}: ${section29Totals[ci]}`).join(" | ")}
        </Typography>
      </Box>

      {hasMismatch && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Section 36 totals do not match Section 29 for this experiment. Please keep the totals aligned
          across both sections.
        </Alert>
      )}

      {severityWarnings.length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          {severityWarnings.map((warning) => (
            <Alert key={warning.code} severity={warning.severity}>
              {warning.message}
            </Alert>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.75 }}>
          Severity review note {recommendedJustification ? "(recommended)" : "(optional)"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Use this field to explain any classification that might look surprising to a reviewer, for example a low
          severity despite surgery or another invasive procedure. Current maximum severity:{" "}
          <strong>{maxSeverity?.label || "not specified yet"}</strong>.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Severity justification / clarification"
          placeholder="Explain why the selected severity degree is appropriate for this experiment."
          value={exp.severityJustification || ""}
          onChange={(e) => {
            const newData = [...data];
            newData[index] = { ...newData[index], severityJustification: e.target.value };
            setData(newData);
          }}
        />
      </Box>
    </Box>
  );
}

export default Section36Form;
